import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Popover, Tooltip, CircularProgress } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ReleaseService, { RELEASE_MEDIA, FEED_PAGE_SIZE } from '../../../Services/release.service';
import XtramMark from './XtramMark';
import ReleasesIcon from './ReleasesIcon';
import ReleaseDetailsDialog from './ReleaseDetailsDialog';
import Styles from './WhatsNew.module.css';

const LAST_SEEN_KEY = 'xtram_whats_new_last_seen_at';
const IDLE_TIP_SESSION_KEY = 'xtram_whats_new_idle_tip_shown';
// With no history yet, only releases from the last month count as "new".
const FIRST_VISIT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
// Staggered after the help button's own auto tooltip (1s – 5.5s) so the two never overlap.
const AUTO_TIP_SHOW_MS = 6000;
const AUTO_TIP_HIDE_MS = 11500;
const SEARCH_THRESHOLD = 5;
const LOAD_MORE_SCROLL_OFFSET = 80;

// Server timestamps are UTC without an offset suffix.
export const releaseTime = (value) => (value ? moment.utc(value).valueOf() : 0);

const byNewest = (a, b) => releaseTime(b.publishedAtUtc) - releaseTime(a.publishedAtUtc);

const readStorage = (storage, key) => {
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (storage, key, value) => {
    try { storage.setItem(key, value); } catch { /* storage unavailable */ }
};

const readLastSeen = () => {
    const raw = readStorage(localStorage, LAST_SEEN_KEY);
    return raw ? Number(raw) : null;
};

const isUnseen = (release, lastSeen) => {
    const at = releaseTime(release.publishedAtUtc);
    return lastSeen == null ? at >= Date.now() - FIRST_VISIT_WINDOW_MS : at > lastSeen;
};

const tooltipSlotProps = {
    popper: {
        modifiers: [
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'flip', enabled: false },
        ],
    },
    tooltip: {
        sx: {
            fontFamily: 'Heebo, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            bgcolor: 'var(--xtram-blue-app, #304fff)',
            px: 1.5,
            py: 0.75,
            maxWidth: 260,
        },
    },
    arrow: { sx: { color: 'var(--xtram-blue-app, #304fff)' } },
};

const ReleaseThumb = ({ release }) => {
    if (release.mediaUrl && release.mediaType === RELEASE_MEDIA.Video) {
        return (
            <span className={Styles.thumb}>
                <video src={release.mediaUrl} muted playsInline preload="metadata" className={Styles.thumbMedia} />
                <span className={Styles.thumbPlay}><PlayArrowRoundedIcon sx={{ fontSize: 20 }} /></span>
            </span>
        );
    }
    if (release.mediaUrl) {
        return (
            <span className={Styles.thumb}>
                <img src={release.mediaUrl} alt="" className={Styles.thumbMedia} loading="lazy" />
                {release.mediaType === RELEASE_MEDIA.Gif && <span className={Styles.thumbTag}>GIF</span>}
            </span>
        );
    }
    return (
        <span className={`${Styles.thumb} ${Styles.thumbPlaceholder}`}>
            <XtramMark size={26} mono="#ffffff" />
        </span>
    );
};

const ReleaseItem = ({ release, isNew, onOpen, t }) => (
    <button type="button" className={Styles.item} onClick={onOpen}>
        <ReleaseThumb release={release} />
        <span className={Styles.itemBody}>
            <span className={Styles.itemTitleRow}>
                <span className={Styles.itemTitle}>{release.title}</span>
                {isNew && <span className={Styles.itemNew}>{t('New')}</span>}
            </span>
            {release.summary && <span className={Styles.itemSummary}>{release.summary}</span>}
            <span className={Styles.itemDate}>{moment.utc(release.publishedAtUtc).local().format('LL')}</span>
        </span>
    </button>
);

function WhatsNewButton({ direction = 'rtl' }) {
    const { t } = useTranslation();
    const [releases, setReleases] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [query, setQuery] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [tipOpen, setTipOpen] = useState(false);
    const [lastSeen, setLastSeen] = useState(readLastSeen);
    // Ids that were unseen when the panel opened, so their "New" tag survives marking them as read.
    const [newIds, setNewIds] = useState(() => new Set());
    const [selectedIndex, setSelectedIndex] = useState(null);
    const loadingMoreRef = useRef(false);

    const load = useCallback(async (force = false) => {
        setLoading(true);
        setError(false);
        try {
            const data = await ReleaseService.getFeed({ force });
            setReleases([...data].sort(byNewest));
            setHasMore(data.length >= FEED_PAGE_SIZE);
        } catch (e) {
            console.error('WhatsNew: failed to load releases', e);
            setError(true);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || !hasMore) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
            const page = await ReleaseService.getFeedPage(releases.length);
            setReleases((prev) => {
                const known = new Set(prev.map((r) => r.id));
                return [...prev, ...page.filter((r) => !known.has(r.id))].sort(byNewest);
            });
            setHasMore(page.length >= FEED_PAGE_SIZE);
        } catch (e) {
            console.error('WhatsNew: failed to load more releases', e);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [hasMore, releases.length]);

    const unseen = useMemo(() => releases.filter((r) => isUnseen(r, lastSeen)), [releases, lastSeen]);
    const unseenCount = unseen.length;
    const hasNew = unseenCount > 0;
    const isPanelOpen = Boolean(anchorEl);

    useEffect(() => {
        if (!loaded) return undefined;
        const idleTipAlreadyShown = readStorage(sessionStorage, IDLE_TIP_SESSION_KEY);
        if (!hasNew && idleTipAlreadyShown) return undefined;

        const show = setTimeout(() => {
            setTipOpen(true);
            if (!hasNew) writeStorage(sessionStorage, IDLE_TIP_SESSION_KEY, '1');
        }, AUTO_TIP_SHOW_MS);
        const hide = setTimeout(() => setTipOpen(false), AUTO_TIP_HIDE_MS);
        return () => {
            clearTimeout(show);
            clearTimeout(hide);
        };
    }, [loaded, hasNew]);

    const markAllSeen = () => {
        const newest = releases.reduce((max, r) => Math.max(max, releaseTime(r.publishedAtUtc)), 0);
        const stamp = Math.max(newest, lastSeen ?? 0);
        writeStorage(localStorage, LAST_SEEN_KEY, String(stamp));
        setLastSeen(stamp);
    };

    const handleOpen = (event) => {
        setTipOpen(false);
        setQuery('');
        setNewIds(new Set(unseen.map((r) => r.id)));
        setAnchorEl(event.currentTarget);
        if (releases.length > 0) markAllSeen();
        load();
    };

    const handleClose = () => setAnchorEl(null);

    const handleListScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_SCROLL_OFFSET) loadMore();
    };

    const trimmedQuery = query.trim().toLowerCase();
    const visible = useMemo(() => {
        if (!trimmedQuery) return releases;
        return releases.filter((r) =>
            `${r.title || ''} ${r.summary || ''}`.toLowerCase().includes(trimmedQuery)
        );
    }, [releases, trimmedQuery]);

    // Releases are sorted newest-first, so the "New" group is always a prefix of the list.
    const newSection = trimmedQuery ? [] : visible.filter((r) => newIds.has(r.id));
    const earlierSection = trimmedQuery ? visible : visible.filter((r) => !newIds.has(r.id));
    const showSectionTitles = newSection.length > 0 && earlierSection.length > 0;
    const showSearch = releases.length > SEARCH_THRESHOLD || hasMore;

    const isRtl = direction === 'rtl';
    const countLabel = unseenCount > 9 ? '9+' : String(unseenCount);
    const latestUnseen = unseen[0];

    const tooltipTitle = (
        <span className={Styles.tipContent}>
            <span className={Styles.tipHeadline}>{t('WhatsNewAutoLabel')}</span>
            {hasNew && (
                <span className={Styles.tipDetail}>
                    {unseenCount > 1
                        ? t('WhatsNewTooltipCount', { count: unseenCount })
                        : (latestUnseen?.title || t('WhatsNewTooltipNew'))}
                </span>
            )}
        </span>
    );

    const renderItem = (release) => (
        <ReleaseItem
            key={release.id}
            release={release}
            isNew={newIds.has(release.id)}
            onOpen={() => setSelectedIndex(visible.indexOf(release))}
            t={t}
        />
    );

    return (
        <>
            <Tooltip
                title={tooltipTitle}
                open={tipOpen && !isPanelOpen}
                onOpen={() => setTipOpen(true)}
                onClose={() => setTipOpen(false)}
                arrow
                placement="bottom"
                disableFocusListener
                slotProps={tooltipSlotProps}
            >
                <button
                    type="button"
                    className={`${Styles.whatsNewBtn} ${hasNew ? Styles.whatsNewBtnActive : Styles.whatsNewBtnIdle}`}
                    onClick={handleOpen}
                    aria-label={hasNew ? `${t('WhatsNew')} (${unseenCount} ${t('New')})` : t('WhatsNew')}
                    aria-haspopup="dialog"
                >
                    <span className={Styles.markWrap}>
                        <ReleasesIcon
                            size={30}
                            className={Styles.icon}
                            bodyClassName={Styles.iconBody}
                            waveClassName={Styles.iconWaves}
                            waveInnerClassName={Styles.waveInner}
                            waveOuterClassName={Styles.waveOuter}
                        />
                        {hasNew && (
                            <>
                                <span className={`${Styles.sparkle} ${Styles.sparkleA}`} aria-hidden="true" />
                                <span className={`${Styles.sparkle} ${Styles.sparkleB}`} aria-hidden="true" />
                            </>
                        )}
                    </span>
                    {hasNew && (
                        <span className={`${Styles.newTag} ${unseenCount > 1 ? '' : Styles.newTagSolo}`} aria-hidden="true">
                            <span className={Styles.newTagText}>{t('New')}</span>
                            {unseenCount > 1 && <span className={Styles.newTagCount}>{countLabel}</span>}
                        </span>
                    )}
                </button>
            </Tooltip>

            <Popover
                open={isPanelOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
                PaperProps={{ className: Styles.panelPaper, sx: { mt: 1 } }}
            >
                <div className={Styles.panel} dir={direction}>
                    <div className={Styles.panelHeader}>
                        <span className={Styles.panelHeaderMark}>
                            <XtramMark size={22} mono="#ffffff" />
                        </span>
                        <div className={Styles.panelHeaderText}>
                            <span className={Styles.panelTitle}>{t('WhatsNewTitle')}</span>
                            <span className={Styles.panelSubtitle}>{t('WhatsNewSubtitle')}</span>
                        </div>
                        {releases.length > 0 && (
                            <span className={Styles.panelCount}>{releases.length}{hasMore ? '+' : ''}</span>
                        )}
                        <button type="button" className={Styles.panelClose} onClick={handleClose} aria-label={t('Close')}>
                            <CloseRoundedIcon sx={{ fontSize: 18 }} />
                        </button>
                    </div>

                    {showSearch && (
                        <div className={Styles.searchRow}>
                            <SearchRoundedIcon sx={{ fontSize: 18, color: '#8a97ad' }} />
                            <input
                                type="search"
                                className={Styles.searchInput}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t('WhatsNewSearch')}
                                aria-label={t('WhatsNewSearch')}
                            />
                        </div>
                    )}

                    <div className={Styles.panelList} onScroll={handleListScroll}>
                        {loading && releases.length === 0 && (
                            <div className={Styles.panelState}><CircularProgress size={26} /></div>
                        )}
                        {!loading && error && releases.length === 0 && (
                            <div className={Styles.panelState}>
                                <span>{t('WhatsNewLoadError')}</span>
                                <button type="button" className={Styles.retryBtn} onClick={() => load(true)}>{t('TryAgain')}</button>
                            </div>
                        )}
                        {!loading && !error && releases.length === 0 && (
                            <div className={Styles.panelState}>{t('WhatsNewEmpty')}</div>
                        )}
                        {releases.length > 0 && visible.length === 0 && (
                            <div className={Styles.panelState}>{t('WhatsNewNoResults')}</div>
                        )}

                        {showSectionTitles && <div className={Styles.sectionTitle}>{t('New')}</div>}
                        {newSection.map(renderItem)}
                        {showSectionTitles && <div className={Styles.sectionTitle}>{t('WhatsNewEarlier')}</div>}
                        {earlierSection.map(renderItem)}

                        {hasMore && releases.length > 0 && (
                            <div className={Styles.loadMoreRow}>
                                {loadingMore ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <button type="button" className={Styles.retryBtn} onClick={loadMore}>
                                        {t('WhatsNewLoadMore')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Popover>

            <ReleaseDetailsDialog
                releases={visible}
                index={selectedIndex}
                onIndexChange={setSelectedIndex}
                onClose={() => setSelectedIndex(null)}
                direction={direction}
            />
        </>
    );
}

export default WhatsNewButton;
